import type { ListedPlugin, PluginScope } from '@gitlab-org/duo-plugin-marketplace/node';
import { bold } from '../../utils/console_style';
import { tildify } from './describe_scope';

function scopeHeader(scope: PluginScope): string {
  return bold(scope);
}

function formatPlugin(plugin: ListedPlugin, currentWorkspacePath: string): string[] {
  const statusLabel = plugin.enabled ? 'enabled' : 'disabled';
  const lines = [
    `  ❯ ${plugin.id}`,
    `    Version: ${plugin.version}`,
    `    Status: ${statusLabel}`,
  ];
  if (plugin.scope !== 'user' && plugin.projectPath) {
    const current = plugin.projectPath === currentWorkspacePath ? ' (current)' : '';
    lines.push(`    Project: ${tildify(plugin.projectPath)}${current}`);
  }
  return lines;
}

function sortProjectGroup(group: ListedPlugin[], currentWorkspacePath: string): void {
  group.sort((a, b) => {
    const aCurrent = a.projectPath === currentWorkspacePath;
    const bCurrent = b.projectPath === currentWorkspacePath;
    if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
    return (a.projectPath ?? '').localeCompare(b.projectPath ?? '');
  });
}

export function formatPluginList(plugins: ListedPlugin[], currentWorkspacePath: string): string[] {
  if (plugins.length === 0) {
    return ['No plugins installed. Use `duo plugin install` to install a plugin.'];
  }

  // Group by scope (insertion-ordered), so scope display order follows the
  // input's first occurrence of each scope but a scope header is never
  // repeated, whatever order the input arrives in.
  const groups = new Map<PluginScope, ListedPlugin[]>();
  for (const plugin of plugins) {
    const group = groups.get(plugin.scope) ?? [];
    group.push(plugin);
    groups.set(plugin.scope, group);
  }

  for (const [scope, group] of groups) {
    if (scope !== 'user') {
      sortProjectGroup(group, currentWorkspacePath);
    }
  }

  const lines: string[] = ['Installed plugins:', ''];

  let first = true;
  for (const [scope, group] of groups) {
    if (!first) {
      lines.push('');
    }
    first = false;
    lines.push(scopeHeader(scope));
    for (const plugin of group) {
      lines.push(...formatPlugin(plugin, currentWorkspacePath));
    }
  }

  return lines;
}
